import React, { useMemo, useState } from 'react';
import { Props as BaseListProps } from '@/components/list';
import { DEFAULT_CURRENCY_CODE, OrderFee } from '@/utils/money';
import { GtinSettings } from '@/utils/gtin-metadata';
import { ImageRow } from '@/components/image-row';
import { ImageViewerModal } from '@/components/image-viewer-modal';
import {
  LIST_ITEM_VERTICAL_PADDING,
  Props as ListItemProps,
} from '@/components/list-item';
import {
  OrderItemList,
  OrderItemListItemType,
} from '@/components/order-item-list';
import {
  OrderItemListFieldsFragment,
  OrderItemRatingFieldsFragment,
} from '@/types';
import { OrderItemNoteSheet } from './order-item-note-sheet';
import { RatingValue } from '@/components/orders/hooks/use-review-form-reducer';
import { Props as RecyclerListProps } from '@/components/recycler-list';
import { ScrollViewProps, StyleSheet, Text, View } from 'react-native';
import { TYPOGRAPHY } from '@/styles';
import { useOrderItemHorizontalSpace } from './hooks/use-order-item-horizontal-space';
import { useTheme } from '@/contexts/theme-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

type OrderItemListProps = Omit<BaseListProps<OrderItemListItemType>, 'data'>;

export type OrderItemDataType = OrderItemListFieldsFragment & {
  order_item_rating?: OrderItemRatingFieldsFragment | null;
};

type Props = OrderItemListProps & {
  displayItemCode?: boolean;
  displayItemPrices?: boolean;
  displayPlaceholder?: boolean;
  gtinSettings?: GtinSettings;
  hideMissingItems?: boolean;
  ListFooterComponent?: RecyclerListProps<OrderItemDataType>['ListFooterComponent'];
  orderFees: OrderFee[];
  orderItems: OrderItemDataType[];
  scrollViewProps?: ScrollViewProps;
  useInvoicePrices: boolean;
};

// Account for two lines of text
const REVIEW_COMMENT_MAX_LINES = 2;
const REVIEW_COMMENT_MARGIN_BOTTOM = 12;

export const OrderDetailList: React.FC<Props> = ({
  displayItemCode = false,
  displayItemPrices = true,
  displayPlaceholder = true,
  gtinSettings,
  hideMissingItems = false,
  ListEmptyComponent,
  ListFooterComponent,
  orderFees,
  orderItems,
  scrollViewProps,
  useInvoicePrices,
}) => {
  const theme = useTheme();

  const [imageURL, setImageURL] = useState<string | null>(null);
  const [selectedNotes, setSelectedNotes] = useState<string | null>(null);
  const { currencyCode } = useVariantPackInfoContext();
  const listItemHorizontalPadding = useOrderItemHorizontalSpace();

  const listItemProps = useMemo<
    Partial<ListItemProps<OrderItemListItemType>>
  >(() => {
    return {
      listItemStyle: (option) => {
        const itemsInOrder = option.metadata!.orderItems;
        const hasNegativeRating = itemsInOrder.some((oi) => {
          const orderItem = oi.item;
          return (
            orderItem.metadata.orderItemRating?.rating ===
            RatingValue.ThumbsDown
          );
        });
        const hasSubstitution = Boolean(
          option.metadata?.substitutionVariantPack,
        );
        return {
          backgroundColor: hasNegativeRating
            ? theme.colors.cautionHighlight
            : theme.colors.white,
          paddingHorizontal: hasSubstitution ? 0 : listItemHorizontalPadding,
          paddingVertical: hasSubstitution ? 4 : LIST_ITEM_VERTICAL_PADDING,
        };
      },
    };
  }, [theme, listItemHorizontalPadding]);

  return (
    <>
      <OrderItemList
        displayItemCode={displayItemCode}
        displayItemPrices={displayItemPrices}
        displayPlaceholder={displayPlaceholder}
        fallbackCurrencyCode={currencyCode || DEFAULT_CURRENCY_CODE}
        gtinSettings={gtinSettings}
        hideMissingItems={hideMissingItems}
        ListEmptyComponent={ListEmptyComponent}
        ListFooterComponent={ListFooterComponent}
        listItemProps={listItemProps}
        onNotesPress={setSelectedNotes}
        orderFees={orderFees}
        orderItems={orderItems}
        renderOrderItemFooter={(option) => {
          const { orderItemRating } = option.metadata;
          return orderItemRating?.rating === RatingValue.ThumbsDown ? (
            <View style={styles.reviewContent}>
              <Text
                numberOfLines={REVIEW_COMMENT_MAX_LINES}
                style={[TYPOGRAPHY.medium, styles.reviewComment]}
              >
                {orderItemRating.comment}
              </Text>
              <ImageRow
                imageURLs={orderItemRating.photo_url_list || []}
                onPressImage={setImageURL}
              />
            </View>
          ) : null;
        }}
        scrollViewProps={scrollViewProps}
        useInvoicePrices={useInvoicePrices}
      />
      {imageURL ? (
        <ImageViewerModal
          imageURLs={[imageURL]}
          isVisible
          onClose={() => setImageURL(null)}
        />
      ) : null}
      {selectedNotes ? (
        <OrderItemNoteSheet
          isVisible
          notes={selectedNotes}
          onClose={() => setSelectedNotes(null)}
        />
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  reviewComment: {
    marginBottom: REVIEW_COMMENT_MARGIN_BOTTOM,
  },
  reviewContent: {
    flex: 1,
    justifyContent: 'flex-end',
  },
});
